import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import {
  calendarBetween, calendarDay, type CalendarDayDetail, type CalendarItem,
} from './api'

/**
 * What happened, everywhere, on a day.
 *
 * ## The question this answers
 *
 * Every feature knows what it did. Nobody could ask the platform what the CAMPAIGN did on
 * the 14th — the rally, the two meetings, the live stream, the forty interviews, the three
 * incidents, the money out — without opening six screens and holding the answer in their
 * head. That is the question a candidate actually asks, usually the morning after.
 *
 * ## Three panes, in the order somebody reads them
 *
 * The grid sizes each day by how much happened, so a glance finds the busy week. Opening a
 * day gives the summary first — how many of each kind of thing, with a way into the feature
 * that owns it — and the timeline beneath it. Somebody who wants the shape of a day reads
 * the summary; somebody who wants the detail reads the list.
 *
 * ## Colour means SOURCE, never importance
 *
 * Each source keeps its colour wherever it appears, so the eye learns "incidents are red"
 * once. A day with four incidents and a day with forty interviews are differently coloured
 * and similarly sized, which is correct: the calendar reports activity, and what matters
 * about it is the owning feature's job to say.
 */

/** Fixed per source, assigned in order and never cycled — the same hue everywhere it shows. */
const SOURCE_COLOUR: Record<string, string> = {
  RALLY: 'var(--chart-1)',
  MEETING: 'var(--chart-2)',
  COVERAGE: 'var(--chart-3)',
  POLLING: 'var(--chart-4)',
  INCIDENT: 'var(--severity-high)',
  SPEND: 'var(--chart-5)',
  WARROOM: 'var(--chart-2)',
  DOSSIER: 'var(--chart-1)',
  COMMS: 'var(--chart-3)',
  /* Programme money is NOT campaign spend and must not read as it. A different hue from
     SPEND, deliberately: one is money to a beneficiary with eligibility and measured impact
     behind it, the other is what the campaign spent on itself and goes in a statutory
     return. Giving them one colour would invite exactly the confusion the two tables exist
     to prevent. */
  PROGRAMME: 'var(--chart-4)',
}

const FALLBACK_COLOUR = 'var(--muted-foreground)'

/** Where "see all of them" goes, when the day's rows link to individual records instead. */
const SOURCE_HOME: Record<string, string> = {
  RALLY: '/finance/events',
  MEETING: '/meetings',
  COVERAGE: '/coverage',
  POLLING: '/polling',
  INCIDENT: '/incidents',
  SPEND: '/finance/ledger',
  WARROOM: '/war-room',
  DOSSIER: '/dossier',
  COMMS: '/comms/campaigns',
  PROGRAMME: '/programs',
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

export function CalendarPage() {
  const canRead = useCan('calendar:read')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const today = new Date()
  const monthParam = params.get('month')
  const month = monthParam ? new Date(`${monthParam}-01T00:00:00`) : today
  const { from, to, cells } = useMemo(() => monthGrid(month), [monthParam])

  // Today is open by default when the month on screen contains it. A calendar that opened
  // on nothing would make somebody click before it told them anything, and the overwhelming
  // first question is "what happened today".
  const defaultDay = cells.some((c) => c.date === iso(today) && c.inMonth)
    ? iso(today) : null
  const dayParam = params.get('day') ?? defaultDay

  const [sources, setSources] = useState<string[]>([])
  const range = useLoad(
    () => calendarBetween({ from, to, source: sources.length ? sources : undefined }),
    [from, to, sources.join(',')])

  if (!canRead) return <Forbidden what="The calendar" />

  const data = range.data
  const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]))
  const busiest = Math.max(1, ...(data?.days ?? []).map((d) => d.total))

  // The open day is its own request. The grid needs forty-two numbers; only this view reads
  // rows, so only this view fetches them.
  const detail = useLoad(
    () => (dayParam ? calendarDay({ date: dayParam }) : Promise.resolve(null)), [dayParam])

  const goToMonth = (year: number, monthIndex: number) => setParams((p) => {
    const q = new URLSearchParams(p)
    q.set('month', `${year}-${String(monthIndex + 1).padStart(2, '0')}`)
    q.delete('day')
    return q
  }, { replace: true })

  const step = (delta: number) => {
    const next = new Date(month.getFullYear(), month.getMonth() + delta, 1)
    goToMonth(next.getFullYear(), next.getMonth())
  }

  const openOn = (date: string | null) => setParams((p) => {
    const q = new URLSearchParams(p)
    if (date) q.set('day', date)
    else q.delete('day')
    return q
  }, { replace: true })

  // Twelve years back and two forward: an election cycle plus the campaign's own history,
  // which is as far as any of this data goes.
  const years: number[] = []
  for (let y = today.getFullYear() - 12; y <= today.getFullYear() + 2; y++) years.push(y)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Calendar"
        description="Everything the campaign did, from every part of the platform, on one
                     grid. Open a day to see all of it — rallies, meetings, live coverage,
                     interviews, incidents, what the war room picked up and money out."
        right={(
          <div className="flex flex-wrap items-center gap-1.5">
            <button type="button" onClick={() => step(-1)} aria-label="Previous month"
                    className="erp-btn-secondary h-9 px-3 text-erp-sm">←</button>
            {/* Jump anywhere, rather than clicking back through a year one month at a time. */}
            <select
              value={month.getMonth()}
              onChange={(e) => goToMonth(month.getFullYear(), Number(e.target.value))}
              aria-label="Month"
              className="h-9 rounded border border-border bg-background px-2 text-erp-sm"
            >
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select
              value={month.getFullYear()}
              onChange={(e) => goToMonth(Number(e.target.value), month.getMonth())}
              aria-label="Year"
              className="h-9 rounded border border-border bg-background px-2 text-erp-sm
                         tabular-nums"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button type="button" onClick={() => step(1)} aria-label="Next month"
                    className="erp-btn-secondary h-9 px-3 text-erp-sm">→</button>
            <button type="button"
                    onClick={() => {
                      goToMonth(today.getFullYear(), today.getMonth())
                      openOn(iso(today))
                    }}
                    className="erp-btn-secondary h-9 px-3 text-erp-sm">Today</button>
          </div>
        )}
      />

      <ErrorStrip message={range.error} />

      {data && data.sources.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {data.sources.map((s) => {
            const on = sources.length === 0 || sources.includes(s.code)
            return (
              <button
                key={s.code}
                type="button"
                onClick={() => setSources((cur) => {
                  const all = data.sources.map((x) => x.code)
                  const base = cur.length === 0 ? all : cur
                  return base.includes(s.code)
                    ? base.filter((c) => c !== s.code)
                    : [...base, s.code]
                })}
                className={cn('flex items-center gap-1.5 rounded border px-2 py-1 text-erp-xs',
                  on ? 'border-foreground' : 'border-border text-muted-foreground')}
              >
                <i className="block size-2 rounded-[2px]"
                   style={{ background: SOURCE_COLOUR[s.code] ?? FALLBACK_COLOUR,
                            opacity: on ? 1 : 0.35 }} />
                {s.label}
              </button>
            )
          })}
          {sources.length > 0 && (
            <button type="button" onClick={() => setSources([])}
                    className="text-erp-xs underline underline-offset-2 text-muted-foreground">
              Show all
            </button>
          )}
        </div>
      )}

      {data && data.unavailable.length > 0 && (
        <p className="text-erp-xs text-amber-600 dark:text-amber-500">
          Could not read {data.unavailable.join(', ').toLowerCase()} for this month — what is
          shown below is everything else.
        </p>
      )}

      {range.loading && !data ? <LoadingBlock label="Gathering the month…" /> : (
        <Panel>
          <div className="grid grid-cols-7 border-b border-border">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="px-2 py-1.5 text-erp-xs font-medium
                                      text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((cell) => {
              const day = byDate.get(cell.date)
              const isOpen = cell.date === dayParam
              const isToday = cell.date === iso(today)
              return (
                <button
                  key={cell.date}
                  type="button"
                  onClick={() => openOn(isOpen ? null : cell.date)}
                  className={cn(
                    'min-h-[5.5rem] border-b border-r border-border p-1.5 text-left align-top',
                    !cell.inMonth && 'bg-muted/30',
                    isOpen && 'ring-2 ring-inset ring-foreground',
                    'hover:bg-muted/50',
                  )}
                >
                  <span className={cn('inline-flex min-w-5 justify-center rounded text-erp-xs',
                    'tabular-nums',
                    isToday && 'bg-foreground text-background font-medium',
                    cell.inMonth ? 'font-medium' : 'text-muted-foreground')}>
                    {cell.dayOfMonth}
                  </span>
                  {day && day.total > 0 ? (
                    <>
                      <span className="mt-1 block h-1 rounded-full bg-foreground/20"
                            style={{ width: `${Math.max(12, (day.total / busiest) * 100)}%` }} />
                      <span className="mt-1 flex flex-wrap gap-0.5">
                        {Object.entries(day.bySource).map(([code]) => (
                          <span key={code}
                                className="block size-1.5 rounded-[2px]"
                                style={{ background: SOURCE_COLOUR[code] ?? FALLBACK_COLOUR }} />
                        ))}
                      </span>
                      <span className="mt-0.5 block text-erp-xs text-muted-foreground
                                       tabular-nums">{fmtNumber(day.total)}</span>
                    </>
                  ) : null}
                </button>
              )
            })}
          </div>
        </Panel>
      )}

      {dayParam && (detail.loading && !detail.data
        ? <LoadingBlock label="Opening the day…" />
        : detail.data
          ? <DayView day={detail.data} onOpen={(r) => navigate(r)} />
          : null)}
    </div>
  )
}

/** How many rows a page of the day reveals at a time. */
const PAGE = 50

/**
 * One day: the shape of it, then the detail.
 *
 * ## Clicking a source card filters the list
 *
 * That is the instinct — somebody reading "14 incidents" wants the fourteen, not a trip to
 * another module. So the card body toggles the filter, and the separate "Open <feature>"
 * link is the thing that navigates. One of those is reading the day and the other is
 * leaving it; they should not be the same click.
 *
 * ## A thousand rows are not rendered
 *
 * Polling day puts hundreds of things on one date. The list reveals in pages rather than
 * dumping all of it: the summary already answers "what kind of day was this", the filter
 * narrows to the kind somebody cares about, and the rest arrives when it is asked for.
 * Whatever the server capped is stated — a list that silently stopped would disagree with
 * the counts sitting beside it.
 */
function DayView({ day, onOpen }: {
  day: CalendarDayDetail
  onOpen: (route: string) => void
}) {
  const [only, setOnly] = useState<string | null>(null)
  const [limit, setLimit] = useState(PAGE)

  const labelOf = (code: string) =>
    day.sources.find((s) => s.code === code)?.label ?? code

  const groups = Object.entries(day.bySource)
    .map(([code, count]) => ({
      code,
      count,
      items: day.items.filter((i) => i.sourceCode === code),
    }))
    .sort((a, b) => b.count - a.count)

  const filtered = only ? day.items.filter((i) => i.sourceCode === only) : day.items
  const shown = filtered.slice(0, limit)
  const date = new Date(`${day.date}T00:00:00`)
  const droppedHere = only
    ? (day.truncated[only] ?? 0)
    : Object.values(day.truncated).reduce((a, b) => a + b, 0)

  return (
    <div className="grid gap-3 lg:grid-cols-[22rem_1fr]">
      <Panel title={date.toLocaleDateString(undefined,
        { weekday: 'long', day: 'numeric', month: 'long' })}
             description={`${fmtNumber(day.total)} ${day.total === 1 ? 'thing' : 'things'} `
               + `across ${groups.length} ${groups.length === 1 ? 'area' : 'areas'}`}>
        <ul className="divide-y divide-border">
          {groups.map((g) => {
            const active = only === g.code
            return (
              <li key={g.code} className={cn('p-3', active && 'bg-muted/60')}>
                <button
                  type="button"
                  onClick={() => { setOnly(active ? null : g.code); setLimit(PAGE) }}
                  className="w-full text-left"
                  aria-pressed={active}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-erp-sm font-medium">
                      <i className="block size-2 rounded-[2px]"
                         style={{ background: SOURCE_COLOUR[g.code] ?? FALLBACK_COLOUR }} />
                      {labelOf(g.code)}
                    </span>
                    <span className="text-erp-lg font-semibold tabular-nums">
                      {fmtNumber(g.count)}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-erp-xs text-muted-foreground">
                    {meta(g.items)}
                  </span>
                  <span className="mt-0.5 block text-erp-xs text-muted-foreground">
                    {active ? 'Showing only these — click again for everything'
                      : 'Click to see only these'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onOpen(g.items[0]?.route ?? SOURCE_HOME[g.code] ?? '/')}
                  className="mt-1.5 text-erp-xs underline underline-offset-2
                             text-muted-foreground hover:text-foreground"
                >
                  Open {labelOf(g.code).toLowerCase()} →
                </button>
              </li>
            )
          })}
        </ul>
      </Panel>

      <Panel
        title={only ? `${labelOf(only)} on this day` : 'Through the day'}
        right={only ? (
          <button type="button" onClick={() => { setOnly(null); setLimit(PAGE) }}
                  className="text-erp-xs underline underline-offset-2 text-muted-foreground">
            Show everything
          </button>
        ) : undefined}
      >
        {filtered.length === 0 ? (
          <p className="p-3 text-erp-sm text-muted-foreground">
            Nothing was recorded anywhere on the platform on this day.
          </p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {shown.map((item, i) => (
                <Row key={`${item.sourceCode}-${item.id ?? i}`} item={item} onOpen={onOpen} />
              ))}
            </ul>
            {(shown.length < filtered.length || droppedHere > 0) && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t
                              border-border px-3 py-2">
                <span className="text-erp-xs text-muted-foreground">
                  Showing {fmtNumber(shown.length)} of {fmtNumber(filtered.length)}
                  {droppedHere > 0
                    ? ` · ${fmtNumber(droppedHere)} more exist but were not sent — narrow by `
                      + 'source, or open the feature itself'
                    : ''}
                </span>
                {shown.length < filtered.length && (
                  <button type="button" onClick={() => setLimit((n) => n + PAGE)}
                          className="erp-btn-secondary h-8 text-erp-xs">
                    Show {fmtNumber(Math.min(PAGE, filtered.length - shown.length))} more
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  )
}

/**
 * The one line of meta that is worth having for a source.
 *
 * Built from the day's own items rather than fetched: every number here is already on the
 * page, and a second round trip to restate it would be the page asking the server what it
 * had just been told.
 *
 * `sourceCode` is deliberately not a parameter — a per-source switch here would put every
 * feature's vocabulary in the calendar, which is the coupling the contributor port exists
 * to avoid.
 */
function meta(items: CalendarItem[]): string {
  if (items.length === 0) return ''

  // Aggregated sources already carry their own summary line — use it rather than inventing
  // a worse one from a count of one row.
  if (items.length === 1 && items[0]!.id === null) {
    return items[0]!.detail ?? items[0]!.title
  }

  const statuses = new Map<string, number>()
  for (const item of items) {
    const key = (item.statusCode ?? '').toLowerCase().replace(/_/g, ' ')
    if (key) statuses.set(key, (statuses.get(key) ?? 0) + 1)
  }
  if (statuses.size === 0) {
    return items.map((i) => i.title).slice(0, 2).join(' · ')
  }
  return [...statuses.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([status, n]) => `${n} ${status}`)
    .join(' · ')
}

function Row({ item, onOpen }: { item: CalendarItem, onOpen: (route: string) => void }) {
  const time = item.occurredAt
    ? new Date(item.occurredAt).toLocaleTimeString(undefined,
        { hour: '2-digit', minute: '2-digit' })
    /* All day, not midnight. An expense is incurred on a day, not at 00:00, and printing a
       time it never had would be inventing precision. */
    : 'All day'

  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <span className="w-16 shrink-0 text-erp-xs tabular-nums text-muted-foreground">
        {time}
      </span>
      <i className="mt-1 block size-2 shrink-0 rounded-[2px]"
         style={{ background: SOURCE_COLOUR[item.sourceCode] ?? FALLBACK_COLOUR }} />
      <span className="min-w-0 flex-1">
        <span className="block text-erp-sm">{item.title}</span>
        {item.detail || item.unitName ? (
          <span className="block text-erp-xs text-muted-foreground">
            {[item.detail, item.unitName].filter(Boolean).join(' · ')}
          </span>
        ) : null}
      </span>
      {item.statusCode ? (
        <span className="shrink-0 text-erp-xs text-muted-foreground">
          {item.statusCode.toLowerCase().replace(/_/g, ' ')}
        </span>
      ) : null}
      {/* An explicit button rather than the whole row being a link: the row is something to
          read, and a reader who skims seventy should not keep landing in another module
          because they clicked to scroll. */}
      {item.route ? (
        <button type="button" onClick={() => onOpen(item.route as string)}
                className="shrink-0 rounded border border-border px-2 py-0.5 text-erp-xs
                           text-muted-foreground hover:bg-muted hover:text-foreground">
          View
        </button>
      ) : null}
    </li>
  )
}

/**
 * The six-week grid a month is drawn on, Monday-first.
 *
 * Leading and trailing days from the neighbouring months are included and marked, because a
 * grid that left them blank would make the first week look empty rather than partial.
 */
function monthGrid(month: Date) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  // JS weeks start on Sunday; Kenyan calendars start on Monday.
  const offset = (first.getDay() + 6) % 7
  const start = new Date(first)
  start.setDate(first.getDate() - offset)

  const cells: { date: string, dayOfMonth: number, inMonth: boolean }[] = []
  const cursor = new Date(start)
  for (let i = 0; i < 42; i++) {
    cells.push({
      date: iso(cursor),
      dayOfMonth: cursor.getDate(),
      inMonth: cursor.getMonth() === month.getMonth(),
    })
    cursor.setDate(cursor.getDate() + 1)
  }
  // 42 cells are always written by the loop above, so these are never undefined — but
  // noUncheckedIndexedAccess cannot know that, and asserting is cheaper than widening the
  // return type and making every caller handle an empty month that cannot happen.
  return { from: cells[0]!.date, to: cells[cells.length - 1]!.date, cells }
}

/** Local date, never toISOString — that converts to UTC and shifts the day east of GMT. */
function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    + `-${String(d.getDate()).padStart(2, '0')}`
}
